import React, {
    FC,
    ChangeEvent,
    MouseEvent,
    useRef,
    useMemo,
    useState,
} from "react";
import InlineSvg from "Shared/components/inline-svg";
import Animation from "Shared/components/animation";
import { useMobileMode, useOnClickOutside } from "Shared/util/custom-hooks";
import styles from "./select.css";

type SelectItemValue = string | number;

export type SelectItem<TValue extends SelectItemValue> = {
    disabled?: boolean;
    label: string;
    value: TValue;
};

export type SelectProps<TValue extends SelectItemValue> = {
    className?: string;
    disabled?: boolean;
    items: SelectItem<TValue>[];
    label?: string;
    onChange: (value: TValue) => void;
    placeholder?: string;
    selectedValue?: SelectItemValue;
};

type NativeSelectProps<TValue extends SelectItemValue> = {
    className?: string;
    disabled?: boolean;
    items: SelectItem<TValue>[];
    label?: string;
    onChange: (value: TValue) => void;
    placeholder?: string;
    selectedValue?: TValue;
};

type CustomSelectProps<TValue extends SelectItemValue> = {
    className?: string;
    disabled?: boolean;
    items: SelectItem<TValue>[];
    label?: string;
    onChange: (value: TValue) => void;
    placeholder?: string;
    selectedValue?: TValue;
};

const getLongestLabel = <TValue extends SelectItemValue>(
    items: SelectItem<TValue>[],
    placeholder: string,
) => {
    if (!items) return placeholder;

    const sortedItems = [...items].sort(
        (a, b) => b.label.length - a.label.length,
    );
    const longestItemLabel = sortedItems.length > 0 ? sortedItems[0].label : "";
    return longestItemLabel.length > placeholder.length
        ? longestItemLabel
        : placeholder;
};

const getParsedItemValue = (
    value: SelectItemValue | undefined,
    type: string | undefined,
) =>
    value === undefined || type === undefined
        ? undefined
        : type === "number"
        ? parseInt(value.toString(), 10)
        : value;

const renderToggleArrow = (isOpen: boolean) =>
    isOpen ? (
        <InlineSvg
            src="/caret-down.svg"
            className={`${styles.svgContainer} ${styles.caretUp}`}
        />
    ) : (
        <InlineSvg
            src="/caret-down.svg"
            className={`${styles.svgContainer} ${styles.caretDown}`}
        />
    );

const renderLabel = (text: string) => (
    <div className={styles.selectLabel}>{text}</div>
);

const NativeSelect: FC<NativeSelectProps<any>> = ({
    className,
    disabled,
    items,
    label,
    onChange,
    placeholder,
    selectedValue,
}) => {
    const handleItemChange = (event?: ChangeEvent<HTMLSelectElement>) => {
        const itemValue = event?.currentTarget.value;
        const itemType =
            event?.currentTarget[event?.currentTarget.selectedIndex].dataset
                .itemtype;
        const parsedValue = getParsedItemValue(itemValue, itemType);
        onChange(parsedValue);
    };

    return (
        <div className={`${styles.nativeSelectContainer} ${className || ""}`}>
            {label && renderLabel(label)}
            <div className={styles.selectContainer}>
                <select
                    value={selectedValue || ""}
                    className={`${styles.select} ${
                        disabled ? styles.disabled : ""
                    }`}
                    onChange={handleItemChange}
                    disabled={disabled}
                >
                    {placeholder && (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    )}
                    {items.map((item) => (
                        <option
                            key={item.label}
                            value={item.value}
                            disabled={item.disabled}
                            data-itemtype={typeof item.value}
                        >
                            {item.label}
                        </option>
                    ))}
                </select>
                {renderToggleArrow(false)}
            </div>
        </div>
    );
};

const CustomSelect: FC<CustomSelectProps<any>> = ({
    className,
    disabled,
    items,
    label,
    onChange,
    placeholder,
    selectedValue,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement | null>(null);

    const handleOpenChange = () => setIsOpen(!isOpen);

    useOnClickOutside(selectRef, () => isOpen && handleOpenChange());

    const handleItemChange = (event?: MouseEvent<HTMLButtonElement>) => {
        const dataset = event?.currentTarget.dataset;
        const parsedValue = getParsedItemValue(
            dataset?.itemvalue,
            dataset?.itemtype,
        );
        onChange(parsedValue);
        handleOpenChange();
    };

    const selectedItem = items.find((i) => i.value === selectedValue);
    const selectedLabel = selectedItem ? selectedItem.label : placeholder || "";
    const longestLabel = useMemo(
        () => getLongestLabel(items, placeholder || ""),
        [items, placeholder],
    );

    return (
        <div
            ref={selectRef}
            className={`${styles.customSelectContainer} ${className || ""}`}
        >
            {label && renderLabel(label)}
            <div className={styles.selectContainer}>
                <button
                    type="button"
                    onClick={!disabled ? handleOpenChange : undefined}
                    className={disabled ? styles.disabled : ""}
                >
                    <span
                        className={
                            !!placeholder && selectedLabel === placeholder
                                ? styles.placeholderText
                                : ""
                        }
                    >
                        {selectedLabel}
                    </span>
                </button>
                <div className={styles.hiddenMaxWidth}>
                    <button type="button">{longestLabel}</button>
                </div>
                {renderToggleArrow(isOpen)}
            </div>
            <Animation show={isOpen} animation="slide_down">
                <div className={styles.list}>
                    <ul>
                        {items.map((item) => (
                            <li
                                key={item.value}
                                className={item.disabled ? styles.disabled : ""}
                            >
                                <button
                                    type="button"
                                    data-itemvalue={item.value}
                                    data-itemtype={typeof item.value}
                                    onClick={
                                        !item.disabled
                                            ? handleItemChange
                                            : undefined
                                    }
                                >
                                    {item.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </Animation>
        </div>
    );
};

export const Select = <TValue extends SelectItemValue>({
    className,
    disabled,
    items,
    label,
    onChange,
    placeholder,
    selectedValue,
}: SelectProps<TValue>) => {
    const isMobileMode = useMobileMode();

    return isMobileMode ? (
        <NativeSelect
            label={label}
            items={items}
            selectedValue={selectedValue}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            className={className}
        />
    ) : (
        <CustomSelect
            label={label}
            items={items}
            selectedValue={selectedValue}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            className={className}
        />
    );
};
